import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  process,
  State,
} from '@progress/kendo-data-query';
import { GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  DEFAULT_SUPPLIER_STATUS_FILTER,
  SUPPLIER_TYPE_FILTER_ITEMS,
  SUPPLIERS_DEFAULT_PAGE_SIZE,
} from 'src/constants/manageSuppliers';
import { CanManageOfflineSuppliers } from 'src/constants/permissionsList';
import useAuth from 'src/hooks/useAuth';
import useDebounce from 'src/hooks/useDebounce';
import usePermission from 'src/hooks/usePermissions';
import useRedirect from 'src/hooks/useRedirect';
import {
  SupplierInfoEditPermissions,
  SupplierTransmissionPermissions,
} from 'src/sections/manage_suppliers/utils/supplierInfoPermissions';
import { buildSupplierFilterRequest } from 'src/services/manage_suppliers/buildSupplierFilterRequest';
import { fetchSuppliersForExport as fetchSuppliersForExportRequest } from 'src/services/manage_suppliers/fetchSuppliersForExport';
import useSuppliersQuery from 'src/services/manage_suppliers/useSuppliersQuery';
import useGetProgramInfo from 'src/services/marketplace/useGetProgramInfo';
import { DropDownCategories } from 'src/types/generic';
import {
  ManageSupplier,
  ManageSupplierTypeFilterItem,
} from 'src/types/manage-suppliers/ManageSupplier';
import { normalizeNullOrEmptyFilters } from 'src/utils/normalizeNullOrEmptyFilters';
import { createContext } from 'use-context-selector';

export const initialSuppliersDataState: State = {
  skip: 0,
  take: SUPPLIERS_DEFAULT_PAGE_SIZE,
  sort: [],
  filter: { logic: 'and', filters: [] },
};

interface SuppliersContextValue {
  searchValue: string;
  setSearchValue: (value: string) => void;
  filterBy: string;
  supplierTypeFilter: ManageSupplierTypeFilterItem[];
  setSupplierTypeFilter: (value: ManageSupplierTypeFilterItem[]) => void;
  statusFilter: DropDownCategories;
  setStatusFilter: (value: DropDownCategories) => void;
  dataState: State;
  onDataStateChange: (event: GridDataStateChangeEvent) => void;
  suppliers: ManageSupplier[];
  isExportingSuppliers: boolean;
  fetchSuppliersForExport: () => Promise<ManageSupplier[]>;
  totalRecords: number;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  canManageSuppliers: boolean;
  canManageTransmissionSettings: boolean;
  canEditSupplierInfo: boolean;
  isLoading: boolean;
  refetchSuppliers: () => Promise<unknown>;
  isCreateSupplierPanelOpen: boolean;
  openCreateSupplierPanel: () => void;
  closeCreateSupplierPanel: () => void;
  editingSupplier: ManageSupplier | null;
  isEditSupplierPanelOpen: boolean;
  openEditSupplierPanel: (supplier: ManageSupplier) => void;
  closeEditSupplierPanel: () => void;
  openProgramInfo: (catalogProgramId: string) => void;
  openingProgramInfoId: string | null;
  isLoadingProgramInfo: boolean;
}

export const SuppliersContext = createContext<SuppliersContextValue>({} as SuppliersContextValue);

export const SuppliersContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const { redirect } = useRedirect();
  const { companyId, companies } = useAuth();
  const [searchValue, setSearchValue] = useState('');
  const [supplierTypeFilter, setSupplierTypeFilter] = useState<ManageSupplierTypeFilterItem[]>(
    SUPPLIER_TYPE_FILTER_ITEMS
  );
  const [statusFilter, setStatusFilter] = useState<DropDownCategories>(
    DEFAULT_SUPPLIER_STATUS_FILTER
  );
  const [dataState, setDataState] = useState<State>(initialSuppliersDataState);
  const [isCreateSupplierPanelOpen, setIsCreateSupplierPanelOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<ManageSupplier | null>(null);
  const [isEditSupplierPanelOpen, setIsEditSupplierPanelOpen] = useState(false);
  const [selectedProgramInfoId, setSelectedProgramInfoId] = useState<string | null>(null);
  const canManageSuppliers = usePermission({ permissions: CanManageOfflineSuppliers });
  const canManageTransmissionSettings = usePermission({
    permissions: SupplierTransmissionPermissions,
    or: true,
  });
  const canEditSupplierInfo = usePermission({
    permissions: SupplierInfoEditPermissions,
    or: true,
  });

  const openCreateSupplierPanel = useCallback(() => {
    setIsCreateSupplierPanelOpen(true);
  }, []);

  const closeCreateSupplierPanel = useCallback(() => {
    setIsCreateSupplierPanelOpen(false);
  }, []);

  const openEditSupplierPanel = useCallback((supplier: ManageSupplier) => {
    setEditingSupplier(supplier);
    setIsEditSupplierPanelOpen(true);
  }, []);

  const closeEditSupplierPanel = useCallback(() => {
    setIsEditSupplierPanelOpen(false);
    setEditingSupplier(null);
  }, []);

  const debouncedSearch = useDebounce(searchValue.trim(), 500);

  const request = useMemo(
    () =>
      buildSupplierFilterRequest({
        statusFilter: statusFilter.value,
        supplierTypeFilter,
      }),
    [statusFilter.value, supplierTypeFilter]
  );

  const { data, isLoading, isFetching, refetch } = useSuppliersQuery({
    request,
    filterBy: debouncedSearch,
  });
  const {
    data: programInfo,
    isLoading: isLoadingProgramInfo,
    isError: isErrorProgramInfo,
    isFetched: isProgramInfoFetched,
  } = useGetProgramInfo(selectedProgramInfoId);
  const allSuppliers = useMemo(() => data?.items ?? [], [data?.items]);

  useEffect(() => {
    if (selectedProgramInfoId === null || isLoadingProgramInfo) {
      return;
    }

    if (isErrorProgramInfo) {
      toast.error(t('Unable to open program information. Please connect with the administrator.'));
      setSelectedProgramInfoId(null);
      return;
    }

    if (!isProgramInfoFetched || !programInfo) {
      return;
    }

    if (programInfo.myAvendraLink) {
      redirect(programInfo.myAvendraLink, true, true);
    } else {
      toast.error(t('Unable to open program information. Please connect with the administrator.'));
    }

    setSelectedProgramInfoId(null);
  }, [
    isErrorProgramInfo,
    isLoadingProgramInfo,
    isProgramInfoFetched,
    programInfo,
    redirect,
    selectedProgramInfoId,
    t,
  ]);

  const openProgramInfo = useCallback((catalogProgramId: string) => {
    if (!catalogProgramId) {
      return;
    }

    setSelectedProgramInfoId(catalogProgramId);
  }, []);

  const processDataState = useMemo<State>(
    () => ({
      ...dataState,
      filter: normalizeNullOrEmptyFilters(dataState.filter),
    }),
    [dataState]
  );

  const processedSuppliers = useMemo(
    () => process(allSuppliers, processDataState),
    [allSuppliers, processDataState]
  );

  const suppliers = processedSuppliers.data;
  const totalRecords = processedSuppliers.total;

  const [isExportingSuppliers, setIsExportingSuppliers] = useState(false);

  const fetchSuppliersForExport = useCallback(async (): Promise<ManageSupplier[]> => {
    setIsExportingSuppliers(true);
    const companyIds = companies.map((company) => company.companyId);
    try {
      return await fetchSuppliersForExportRequest({
        statusFilter: statusFilter.value,
        supplierTypeFilter,
        companyId,
        companyIds,
        filterBy: debouncedSearch,
        filter: processDataState.filter,
        sort: processDataState.sort,
      });
    } finally {
      setIsExportingSuppliers(false);
    }
  }, [companies, companyId, debouncedSearch, processDataState, statusFilter.value, supplierTypeFilter]);

  const isLoadingSuppliers = isLoading || isFetching;

  useEffect(() => {
    setDataState((prev) => ({
      ...prev,
      skip: 0,
    }));
  }, [debouncedSearch, supplierTypeFilter, statusFilter.value]);

  const onDataStateChange = useCallback((event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  }, []);

  const isAllSupplierTypesSelected = useMemo(
    () => supplierTypeFilter.filter((item) => item.display).every((item) => item.show),
    [supplierTypeFilter]
  );

  const hasActiveFilters = useMemo(() => {
    const filters = dataState.filter?.filters;
    const hasGridFilters =
      filters?.some((filter: CompositeFilterDescriptor | FilterDescriptor) =>
        'filters' in filter ? filter.filters.length > 0 : true
      ) ?? false;
    const hasSearch = searchValue.trim().length > 0;
    const hasSupplierTypeFilter = !isAllSupplierTypesSelected;
    const hasStatusFilter = statusFilter.value !== DEFAULT_SUPPLIER_STATUS_FILTER.value;
    const hasSort = (dataState.sort?.length ?? 0) > 0;

    return hasGridFilters || hasSearch || hasSupplierTypeFilter || hasStatusFilter || hasSort;
  }, [
    dataState.filter?.filters,
    dataState.sort,
    isAllSupplierTypesSelected,
    searchValue,
    statusFilter.value,
  ]);

  const clearFilters = useCallback(() => {
    setSearchValue('');
    setSupplierTypeFilter(SUPPLIER_TYPE_FILTER_ITEMS);
    setStatusFilter(DEFAULT_SUPPLIER_STATUS_FILTER);
    setDataState((prev) => ({
      ...initialSuppliersDataState,
      take: prev.take ?? initialSuppliersDataState.take,
    }));
  }, []);

  const value = useMemo(
    () => ({
      searchValue,
      setSearchValue,
      filterBy: debouncedSearch,
      supplierTypeFilter,
      setSupplierTypeFilter,
      statusFilter,
      setStatusFilter,
      dataState,
      onDataStateChange,
      suppliers,
      isExportingSuppliers,
      fetchSuppliersForExport,
      totalRecords,
      hasActiveFilters,
      clearFilters,
      canManageSuppliers,
      canManageTransmissionSettings,
      canEditSupplierInfo,
      isLoading: isLoadingSuppliers,
      refetchSuppliers: refetch,
      isCreateSupplierPanelOpen,
      openCreateSupplierPanel,
      closeCreateSupplierPanel,
      editingSupplier,
      isEditSupplierPanelOpen,
      openEditSupplierPanel,
      closeEditSupplierPanel,
      openProgramInfo,
      openingProgramInfoId: selectedProgramInfoId,
      isLoadingProgramInfo,
    }),
    [
      searchValue,
      debouncedSearch,
      supplierTypeFilter,
      setSupplierTypeFilter,
      statusFilter,
      dataState,
      onDataStateChange,
      suppliers,
      isExportingSuppliers,
      fetchSuppliersForExport,
      totalRecords,
      hasActiveFilters,
      clearFilters,
      canManageSuppliers,
      canManageTransmissionSettings,
      canEditSupplierInfo,
      isLoadingSuppliers,
      refetch,
      isCreateSupplierPanelOpen,
      openCreateSupplierPanel,
      closeCreateSupplierPanel,
      editingSupplier,
      isEditSupplierPanelOpen,
      openEditSupplierPanel,
      closeEditSupplierPanel,
      openProgramInfo,
      selectedProgramInfoId,
      isLoadingProgramInfo,
    ]
  );

  return <SuppliersContext.Provider value={value}>{children}</SuppliersContext.Provider>;
};

export default SuppliersContext;
